import React from 'react';
import { useTheme } from '../../context/ThemeContext';

/**
 * Reusable loading skeleton components for better perceived performance
 * Provides various skeleton patterns for different content types
 */

/**
 * Base skeleton component with shimmer animation
 */
const SkeletonBase = ({ className = '', children, ...props }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div 
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`}
      data-testid="loading-skeleton"
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Skeleton for text content
 */
export const SkeletonText = ({ 
  lines = 1, 
  className = '', 
  lineClassName = '',
  spacing = 'space-y-2'
}) => {
  return (
    <div className={`${spacing} ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonBase 
          key={index}
          className={`h-4 ${lineClassName} ${
            index === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'
          }`}
        />
      ))}
    </div>
  );
};

/**
 * Skeleton for circular profile pictures
 */
export const SkeletonAvatar = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
    xl: 'w-24 h-24',
    '2xl': 'w-32 h-32'
  };

  return (
    <SkeletonBase 
      className={`${sizeClasses[size]} rounded-full ${className}`}
    />
  );
};

/**
 * Skeleton for cards/containers
 */
export const SkeletonCard = ({ 
  className = '', 
  children,
  padding = 'p-6'
}) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md ${padding} ${className}`}>
      {children}
    </div>
  );
};

/**
 * Skeleton for buttons
 */
export const SkeletonButton = ({ 
  size = 'md', 
  className = '',
  variant = 'primary'
}) => {
  const sizeClasses = {
    sm: 'h-8 w-20',
    md: 'h-10 w-24',
    lg: 'h-12 w-32'
  };

  return (
    <SkeletonBase 
      className={`${sizeClasses[size]} rounded-lg ${className}`}
    />
  );
};

/**
 * Skeleton for user profile header
 */
export const SkeletonProfileHeader = ({ className = '' }) => {
  return (
    <SkeletonCard className={`text-center ${className}`}>
      <div className="flex flex-col items-center">
        {/* Profile Picture */}
        <SkeletonAvatar size="xl" className="mb-4" />
        
        {/* Name */}
        <SkeletonText 
          lines={1} 
          className="mb-2" 
          lineClassName="h-8 w-48" 
        />
        
        {/* Role */}
        <SkeletonText 
          lines={1} 
          className="mb-2" 
          lineClassName="h-6 w-32" 
        />
        
        {/* Company */}
        <SkeletonText 
          lines={1} 
          className="mb-4" 
          lineClassName="h-5 w-40" 
        />
        
        {/* LinkedIn Button */}
        <SkeletonButton size="sm" />
      </div>
    </SkeletonCard>
  );
};

/**
 * Skeleton for profile content cards
 */
export const SkeletonProfileContent = ({ className = '' }) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`}>
      {/* Professional Information Card */}
      <SkeletonCard>
        <div className="flex items-center mb-4">
          <SkeletonBase className="w-6 h-6 mr-3" />
          <SkeletonText lines={1} lineClassName="h-6 w-40" />
        </div>
        <div className="space-y-4">
          <div>
            <SkeletonText lines={1} lineClassName="h-4 w-24 mb-2" />
            <SkeletonText lines={1} lineClassName="h-5 w-32" />
          </div>
          <div>
            <SkeletonText lines={1} lineClassName="h-4 w-20 mb-2" />
            <SkeletonText lines={1} lineClassName="h-5 w-36" />
          </div>
        </div>
      </SkeletonCard>

      {/* Location Card */}
      <SkeletonCard>
        <div className="flex items-center mb-4">
          <SkeletonBase className="w-6 h-6 mr-3" />
          <SkeletonText lines={1} lineClassName="h-6 w-24" />
        </div>
        <div className="flex items-center">
          <SkeletonBase className="w-4 h-4 mr-2" />
          <SkeletonText lines={1} lineClassName="h-5 w-48" />
        </div>
      </SkeletonCard>
    </div>
  );
};

/**
 * Skeleton for breadcrumb navigation
 */
export const SkeletonBreadcrumb = ({ className = '' }) => {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <SkeletonBase className="w-5 h-5" />
      <SkeletonBase className="w-1 h-4" />
      <SkeletonText lines={1} lineClassName="h-4 w-16" />
      <SkeletonBase className="w-1 h-4" />
      <SkeletonText lines={1} lineClassName="h-4 w-24" />
    </div>
  );
};

/**
 * Enhanced skeleton for list items
 */
export const SkeletonListItem = ({ className = '' }) => {
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <SkeletonBase className="w-4 h-4" />
      <SkeletonText lines={1} lineClassName="h-4 flex-1" />
    </div>
  );
};

/**
 * Enhanced skeleton for error retry section
 */
export const SkeletonErrorRetry = ({ className = '' }) => {
  return (
    <div className={`text-center space-y-4 ${className}`}>
      <SkeletonBase className="w-16 h-16 rounded-full mx-auto" />
      <SkeletonText lines={1} lineClassName="h-6 w-48 mx-auto" />
      <SkeletonText lines={2} lineClassName="h-4 w-64 mx-auto" />
      <div className="flex justify-center space-x-3">
        <SkeletonButton size="md" />
        <SkeletonButton size="md" />
      </div>
    </div>
  );
};

/**
 * Enhanced skeleton with shimmer effect for better perceived performance
 */
export const SkeletonShimmer = ({ className = '', children, ...props }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div 
      className={`relative overflow-hidden bg-gray-200 dark:bg-gray-700 rounded ${className}`}
      {...props}
    >
      {children}
      <div className={`absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r ${
        isDarkMode 
          ? 'from-transparent via-gray-600/20 to-transparent' 
          : 'from-transparent via-white/60 to-transparent'
      }`} />
    </div>
  );
};

/**
 * Complete skeleton for user profile page with enhanced shimmer
 */
export const SkeletonUserProfile = ({ className = '' }) => {
  return (
    <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
      {/* Breadcrumb Skeleton */}
      <div className="mb-6">
        <SkeletonBreadcrumb />
      </div>

      {/* Page Header Skeleton */}
      <div className="mb-8">
        <SkeletonShimmer className="h-8 w-64 mb-2" />
        <SkeletonShimmer className="h-5 w-48" />
      </div>

      {/* Profile Header Skeleton */}
      <div className="mb-6">
        <SkeletonProfileHeader />
      </div>

      {/* Profile Content Skeleton */}
      <SkeletonProfileContent />
    </div>
  );
};

export default SkeletonBase;